<!--
  1.  There are 4 button sizes: small, medium, large and x-large (default).
  2.  The default background-color is black with a red--fire-brick hover, you can override this by setting the backgroundColor property.
  3.  The default color is white with a white hover, you can override this by setting the textColor property.
  4.  The default icon on the left side depends on whether this is an internal or external link.  If needed this can be disabled using enableIconLeft.
  5.  When you add a url that is not a NuxtLink, it automatically gets an external icon on the left side. When you don't add a url at all, it becomes a button.
-->

<template>
  <AtomLink
    :url="url"
    :target="newWindow ? '_blank' : undefined"
    :fallback-component="fallbackComponent"
    :class="[
      'c-button group hover:shadow-none inline-flex items-center text-center rounded-3xl leading-none transform transition duration-200 cursor-pointer font-sans',
      {
        'text-lg px-4 py-6 sm:px-8 font-semibold': size === 'xx-large',
        'text-lg px-4 py-3 sm:px-8 font-semibold': !size || size === 'x-large',
        'c-button--large text-base px-4 py-3 sm:px-8 font-medium': size === 'large',
        'text-sm px-4 sm:px-5 py-2 font-medium': size === 'medium',
        'text-xs px-4 sm:px-5 py-2 font-medium': size === 'small',
        'shadow-md': shadowed,
        'justify-between': justified,
        'justify-center': !justified,
      },
      backgroundColor,
      textColor,
      border,
      classes,
    ]"
    @click="onClick"
  >
    <template v-if="enableIconLeft" #iconLeft="{ component }">
      <AtomIconArrowRight
        v-if="component === 'NuxtLink' || buttonType === 'button'"
        class="transform transition duration-200 group-hover:translate-x-1"
        :class="{ 'w-3 h-3 mr-2': size !== 'x-large', 'w-4 h-4 mr-2': size === 'x-large' }"
      />
      <AtomIconAppExternal v-else class="mr-3" :class="{ 'w-3 h-3': size === 'small' || size === 'medium' || size === 'large', 'w-4 h-4': !size || size === 'x-large' }" />
    </template>
    <slot />
  </AtomLink>
</template>

<script setup lang="ts">
defineProps({
  url: { type: [String, Object], required: false, default: undefined },
  classes: { type: String, required: false, default: undefined },
  backgroundColor: { type: [Object, String], required: false, default: 'bg-black hover:bg-app-primary-1-red--fire-brick' },
  textColor: { type: String, required: false, default: 'text-white hover:text-white' },
  border: { type: [String, Array, Object], required: false, default: undefined },
  size: { type: String, required: false, default: 'x-large' },
  enableIconLeft: { type: Boolean, required: false, default: true },
  buttonType: { type: String, required: false, default: 'button' },
  // Passed straight through to AtomLink. Override to 'span' when nesting this inside an element that's
  // already a <button> (e.g. a FormKit submit) - a <button> can't contain another <button>.
  fallbackComponent: { type: String, required: false, default: 'button' },
  shadowed: { type: Boolean, required: false, default: true },
  justified: { type: Boolean, required: false, default: false },
  newWindow: { type: Boolean, required: false, default: false },
})

const emit = defineEmits(['click'])

function onClick(event: Event) {
  emit('click', event)
}
</script>

<style lang="scss">
.c-button svg {
  @apply shrink-0;
}

.c-button--large {
  min-height: theme('spacing.10');
}
</style>
